import { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { useTeam } from './TeamContext';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  onSnapshot,
  serverTimestamp,
  increment,
  Timestamp,
  limit
} from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig';
import { EQUIPMENT_TYPES, EQUIPMENT_STATUS, INSPECTION_ROUNDS } from '../data/nurseAppModels';
import { notificationModel } from '../data/notificationModel';

const EquipmentContext = createContext();

export const useEquipment = () => {
  const context = useContext(EquipmentContext);
  if (!context) {
    throw new Error('useEquipment must be used within EquipmentProvider');
  }
  return context;
};

export const EquipmentProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const { currentTeam } = useTeam();
  const [equipment, setEquipment] = useState([]);
  const [catalogs, setCatalogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch equipment for current team
  useEffect(() => {
    if (!currentTeam) {
      setEquipment([]);
      setLoading(false);
      return;
    }

    const unsubscribe = onSnapshot(
      query(
        collection(db, 'equipment'),
        where('teamId', '==', currentTeam.id),
        orderBy('createdAt', 'desc')
      ),
      (snapshot) => {
        const equipmentData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        console.log('📦 EquipmentContext: Loaded equipment data:', equipmentData.length, 'items');
        
        // Log all equipment data to see what fields exist
        if (equipmentData.length > 0) {
          console.log('📦 EquipmentContext: Sample equipment data:', equipmentData.slice(0, 2).map(eq => ({
            id: eq.id,
            name: eq.name,
            hasImages: !!eq.images,
            imagesType: Array.isArray(eq.images) ? 'array' : typeof eq.images,
            imagesLength: Array.isArray(eq.images) ? eq.images.length : 'N/A',
            hasThumbnail: !!eq.thumbnail,
            thumbnail: eq.thumbnail,
            allFields: Object.keys(eq)
          })));
        }
        
        // Log equipment with images
        const equipmentWithImages = equipmentData.filter(eq => 
          (eq.images && Array.isArray(eq.images) && eq.images.length > 0) || eq.thumbnail
        );
        console.log('🖼️ EquipmentContext: Equipment with images:', equipmentWithImages.length);
        if (equipmentWithImages.length > 0) {
          console.log('🖼️ EquipmentContext: Sample equipment with images:', equipmentWithImages.slice(0, 3).map(eq => ({
            id: eq.id,
            name: eq.name,
            images: eq.images,
            thumbnail: eq.thumbnail
          })));
        }
        setEquipment(equipmentData);
        setLoading(false);
      },
      (err) => {
        console.error('Error fetching equipment:', err);
        setError(err.message);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [currentTeam]);

  // Fetch catalogs for current team
  useEffect(() => {
    if (!currentTeam) {
      setCatalogs([]);
      return;
    }

    const unsubscribe = onSnapshot(
      query(
        collection(db, 'equipmentCatalogs'),
        where('teamId', '==', currentTeam.id),
        where('status', '==', 'ACTIVE'),
        orderBy('name', 'asc')
      ),
      (snapshot) => {
        const catalogsData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setCatalogs(catalogsData);
      },
      (err) => {
        console.error('Error fetching catalogs:', err);
      }
    );

    return () => unsubscribe();
  }, [currentTeam]);

  // Create Equipment from Catalog
  const createEquipmentFromCatalog = async (catalogId, additionalData = {}) => {
    try {
      if (!currentUser || !currentTeam) {
        throw new Error('กรุณาเข้าสู่ระบบและเลือกทีม');
      }

      const catalogRef = doc(db, 'equipmentCatalogs', catalogId);
      const catalogSnap = await getDoc(catalogRef);

      if (!catalogSnap.exists()) {
        throw new Error('ไม่พบ Catalog');
      }

      const catalogData = catalogSnap.data();

      const newEquipment = {
        teamId: currentTeam.id,
        catalogId: catalogId,
        name: catalogData.name,
        code: catalogData.code,
        category: catalogData.category,
        subCategory: catalogData.subCategory,
        type: catalogData.type,
        size: additionalData.size || catalogData.size,
        unit: catalogData.unit,
        color: additionalData.color || catalogData.color,
        brand: catalogData.brand,
        model: catalogData.model,
        stockQuantity: additionalData.stockQuantity || 0,
        currentStock: 0, // เริ่มต้นที่ 0 จนกว่าจะ approve
        // Include stock settings from catalog
        minStock: additionalData.minStock !== undefined ? additionalData.minStock : (catalogData.minStock || null),
        maxStock: additionalData.maxStock !== undefined ? additionalData.maxStock : (catalogData.maxStock || null),
        reorderPoint: additionalData.reorderPoint !== undefined ? additionalData.reorderPoint : (catalogData.reorderPoint || null),
        barcode: additionalData.barcode || null,
        serialNumber: additionalData.serialNumber || null,
        status: EQUIPMENT_STATUS.PENDING_APPROVAL, // รออนุมัติ
        currentLocation: additionalData.currentLocation || catalogData.defaultLocation,
        department: additionalData.department || catalogData.defaultDepartment,
        ward: additionalData.ward || catalogData.defaultWard,
        createdBy: currentUser.uid,
        createdByName: currentUser.displayName || currentUser.email,
        submittedAt: serverTimestamp(),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      const equipmentRef = await addDoc(collection(db, 'equipment'), newEquipment);

      // Update catalog usage count
      await updateDoc(catalogRef, {
        usageCount: increment(1),
        lastUsedAt: serverTimestamp()
      });

      return { success: true, equipmentId: equipmentRef.id };
    } catch (err) {
      console.error('Error creating equipment:', err);
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  // Create Equipment (without catalog)
  const createEquipment = async (equipmentData) => {
    try {
      if (!currentUser || !currentTeam) {
        throw new Error('กรุณาเข้าสู่ระบบและเลือกทีม');
      }

      const newEquipment = {
        teamId: currentTeam.id,
        name: equipmentData.name,
        code: equipmentData.code,
        category: equipmentData.category,
        subCategory: equipmentData.subCategory || null,
        type: equipmentData.type,
        size: equipmentData.size || null,
        unit: equipmentData.unit,
        color: equipmentData.color || null,
        brand: equipmentData.brand || null,
        model: equipmentData.model || null,
        stockQuantity: equipmentData.type === EQUIPMENT_TYPES.SMALL_ITEM ? equipmentData.stockQuantity || 0 : null,
        currentStock: 0, // เริ่มต้นที่ 0 จนกว่าจะ approve
        barcode: equipmentData.type === EQUIPMENT_TYPES.LARGE_ITEM ? equipmentData.barcode || null : null,
        serialNumber: equipmentData.serialNumber || null,
        status: EQUIPMENT_STATUS.PENDING_APPROVAL, // รออนุมัติ
        currentLocation: equipmentData.currentLocation || '',
        department: equipmentData.department || '',
        ward: equipmentData.ward || '',
        images: equipmentData.images && Array.isArray(equipmentData.images) ? equipmentData.images : [],
        thumbnail: equipmentData.images && Array.isArray(equipmentData.images) && equipmentData.images.length > 0 
          ? equipmentData.images[0] 
          : null,
        createdBy: currentUser.uid,
        createdByName: currentUser.displayName || currentUser.email,
        submittedAt: serverTimestamp(),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      const equipmentRef = await addDoc(collection(db, 'equipment'), newEquipment);

      return { success: true, equipmentId: equipmentRef.id };
    } catch (err) {
      console.error('Error creating equipment:', err);
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  // Stock In Transaction
  const stockIn = async (equipmentId, transactionData) => {
    try {
      if (!currentUser || !currentTeam) {
        throw new Error('กรุณาเข้าสู่ระบบและเลือกทีม');
      }

      const equipmentRef = doc(db, 'equipment', equipmentId);
      const equipmentSnap = await getDoc(equipmentRef);

      if (!equipmentSnap.exists()) {
        throw new Error('ไม่พบอุปกรณ์');
      }

      const equipmentData = equipmentSnap.data();

      // Create stock transaction
      const transaction = {
        teamId: currentTeam.id,
        equipmentId: equipmentId,
        type: 'STOCK_IN',
        quantity: transactionData.quantity,
        unit: equipmentData.unit,
        performedBy: currentUser.uid,
        performedByName: currentUser.displayName || currentUser.email,
        shiftRound: transactionData.shiftRound || null,
        date: transactionData.date || serverTimestamp(),
        source: transactionData.source || 'PURCHASE',
        sourceDetails: transactionData.sourceDetails || {},
        notes: transactionData.notes || '',
        status: 'PENDING',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      const transactionRef = await addDoc(collection(db, 'equipmentStockTransactions'), transaction);

      return { success: true, transactionId: transactionRef.id };
    } catch (err) {
      console.error('Error creating stock in:', err);
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  // Approve Stock In
  const approveStockIn = async (transactionId) => {
    try {
      if (!currentUser || !currentTeam) {
        throw new Error('กรุณาเข้าสู่ระบบและเลือกทีม');
      }

      // Check if user is Leader or Deputy Leader
      const userTeamRole = currentTeam.userRole;
      if (userTeamRole !== 'LEADER' && userTeamRole !== 'DEPUTY_LEADER') {
        throw new Error('คุณไม่มีสิทธิ์อนุมัติ');
      }

      const transactionRef = doc(db, 'equipmentStockTransactions', transactionId);
      const transactionSnap = await getDoc(transactionRef);

      if (!transactionSnap.exists()) {
        throw new Error('ไม่พบรายการ');
      }

      const transactionData = transactionSnap.data();

      // Check if already approved or rejected
      if (transactionData.status === 'APPROVED') {
        throw new Error('รายการนี้ได้รับการอนุมัติแล้ว');
      }
      if (transactionData.status === 'REJECTED') {
        throw new Error('รายการนี้ถูกปฏิเสธแล้ว ไม่สามารถอนุมัติได้');
      }

      const equipmentRef = doc(db, 'equipment', transactionData.equipmentId);
      const equipmentSnap = await getDoc(equipmentRef);

      if (!equipmentSnap.exists()) {
        throw new Error('ไม่พบอุปกรณ์');
      }

      const equipmentData = equipmentSnap.data();

      // Update equipment stock (only add if not already approved)
      const newStock = (equipmentData.currentStock || 0) + transactionData.quantity;
      await updateDoc(equipmentRef, {
        currentStock: newStock,
        updatedAt: serverTimestamp()
      });

      // Update transaction status
      await updateDoc(transactionRef, {
        status: 'APPROVED',
        approvedBy: currentUser.uid,
        approvedByName: currentUser.displayName || currentUser.email,
        approvedAt: serverTimestamp()
      });

      return { success: true };
    } catch (err) {
      console.error('Error approving stock in:', err);
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  // Approve Equipment (Leader only)
  const approveEquipment = async (equipmentId) => {
    try {
      if (!currentUser || !currentTeam) {
        throw new Error('กรุณาเข้าสู่ระบบและเลือกทีม');
      }

      // Check if user is Leader or Deputy Leader
      const userTeamRole = currentTeam.userRole;
      if (userTeamRole !== 'LEADER' && userTeamRole !== 'DEPUTY_LEADER') {
        throw new Error('คุณไม่มีสิทธิ์อนุมัติ');
      }

      const equipmentRef = doc(db, 'equipment', equipmentId);
      const equipmentSnap = await getDoc(equipmentRef);

      if (!equipmentSnap.exists()) {
        throw new Error('ไม่พบอุปกรณ์');
      }

      const equipmentData = equipmentSnap.data();

      // Only allow approve if status is PENDING_APPROVAL
      if (equipmentData.status !== EQUIPMENT_STATUS.PENDING_APPROVAL) {
        throw new Error('อุปกรณ์นี้ไม่อยู่ในสถานะที่สามารถอนุมัติได้');
      }

      // Update status to AVAILABLE
      await updateDoc(equipmentRef, {
        status: EQUIPMENT_STATUS.AVAILABLE,
        approvedBy: currentUser.uid,
        approvedByName: currentUser.displayName || currentUser.email,
        approvedAt: serverTimestamp(),
        // ถ้ามี stockQuantity ที่ตั้งไว้ ให้ตั้ง currentStock ด้วย
        currentStock: equipmentData.stockQuantity || equipmentData.currentStock || 0,
        updatedAt: serverTimestamp()
      });

      // Send notification to creator
      try {
        if (equipmentData.createdBy && equipmentData.createdBy !== currentUser.uid) {
          const notification = notificationModel.createNotification(
            equipmentData.createdBy,
            notificationModel.types.EQUIPMENT_APPROVED || 'equipment_approved',
            '✅ อุปกรณ์ได้รับการอนุมัติแล้ว',
            `อุปกรณ์ "${equipmentData.name}" ได้รับการอนุมัติแล้ว`,
            {
              equipmentId: equipmentId,
              equipmentName: equipmentData.name,
              approvedBy: currentUser.uid,
              approvedByName: currentUser.displayName || currentUser.email
            }
          );

          await addDoc(collection(db, notificationModel.collection), {
            ...notification,
            read: false,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp()
          });
        }
      } catch (notifError) {
        console.error('Error sending notification:', notifError);
        // Don't fail the approval if notification fails
      }

      return { success: true };
    } catch (err) {
      console.error('Error approving equipment:', err);
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  // Reject Equipment (Leader only)
  const rejectEquipment = async (equipmentId, rejectionReason) => {
    try {
      if (!currentUser || !currentTeam) {
        throw new Error('กรุณาเข้าสู่ระบบและเลือกทีม');
      }

      // Check if user is Leader or Deputy Leader
      const userTeamRole = currentTeam.userRole;
      if (userTeamRole !== 'LEADER' && userTeamRole !== 'DEPUTY_LEADER') {
        throw new Error('คุณไม่มีสิทธิ์ปฏิเสธ');
      }

      const equipmentRef = doc(db, 'equipment', equipmentId);
      const equipmentSnap = await getDoc(equipmentRef);

      if (!equipmentSnap.exists()) {
        throw new Error('ไม่พบอุปกรณ์');
      }

      const equipmentData = equipmentSnap.data();

      // Only allow reject if status is PENDING_APPROVAL
      if (equipmentData.status !== EQUIPMENT_STATUS.PENDING_APPROVAL) {
        throw new Error('อุปกรณ์นี้ไม่อยู่ในสถานะที่สามารถปฏิเสธได้');
      }

      // Update status to REJECTED
      await updateDoc(equipmentRef, {
        status: EQUIPMENT_STATUS.REJECTED,
        rejectedBy: currentUser.uid,
        rejectedByName: currentUser.displayName || currentUser.email,
        rejectedAt: serverTimestamp(),
        rejectionReason: rejectionReason || '',
        updatedAt: serverTimestamp()
      });

      // Send notification to creator
      try {
        if (equipmentData.createdBy && equipmentData.createdBy !== currentUser.uid) {
          const notification = notificationModel.createNotification(
            equipmentData.createdBy,
            notificationModel.types.EQUIPMENT_REJECTED || 'equipment_rejected',
            '❌ อุปกรณ์ถูกปฏิเสธ',
            `อุปกรณ์ "${equipmentData.name}" ถูกปฏิเสธ: ${rejectionReason || 'ไม่มีเหตุผล'}`,
            {
              equipmentId: equipmentId,
              equipmentName: equipmentData.name,
              rejectedBy: currentUser.uid,
              rejectedByName: currentUser.displayName || currentUser.email,
              rejectionReason: rejectionReason || ''
            }
          );

          await addDoc(collection(db, notificationModel.collection), {
            ...notification,
            read: false,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp()
          });
        }
      } catch (notifError) {
        console.error('Error sending notification:', notifError);
        // Don't fail the rejection if notification fails
      }

      return { success: true };
    } catch (err) {
      console.error('Error rejecting equipment:', err);
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  // Update Equipment (แก้ไขแล้วต้อง approve อีกครั้ง)
  const updateEquipment = async (equipmentId, updateData) => {
    try {
      if (!currentUser || !currentTeam) {
        throw new Error('กรุณาเข้าสู่ระบบและเลือกทีม');
      }

      const equipmentRef = doc(db, 'equipment', equipmentId);
      const equipmentSnap = await getDoc(equipmentRef);

      if (!equipmentSnap.exists()) {
        throw new Error('ไม่พบอุปกรณ์');
      }

      const equipmentData = equipmentSnap.data();

      // ตรวจสอบว่าเป็นเจ้าของหรือ Leader/Deputy Leader
      const userTeamRole = currentTeam.userRole;
      const isOwner = equipmentData.createdBy === currentUser.uid;
      const isLeader = userTeamRole === 'LEADER' || userTeamRole === 'DEPUTY_LEADER';

      if (!isOwner && !isLeader) {
        throw new Error('คุณไม่มีสิทธิ์แก้ไขอุปกรณ์นี้');
      }

      // ถ้าเป็น Leader/Deputy Leader แก้ไขได้เลย (ไม่ต้อง approve)
      // ถ้าเป็น Owner และอุปกรณ์ถูก approve แล้ว → ต้อง approve อีกครั้ง
      const needsReapproval = isOwner && equipmentData.status === EQUIPMENT_STATUS.AVAILABLE;

      const updateFields = {
        ...updateData,
        updatedAt: serverTimestamp()
      };

      if (needsReapproval) {
        // เปลี่ยน Status เป็น PENDING_APPROVAL
        updateFields.status = EQUIPMENT_STATUS.PENDING_APPROVAL;
        updateFields.editedBy = currentUser.uid;
        updateFields.editedByName = currentUser.displayName || currentUser.email;
        updateFields.editedAt = serverTimestamp();
        updateFields.previousStatus = EQUIPMENT_STATUS.AVAILABLE; // เก็บสถานะเดิม
      }

      await updateDoc(equipmentRef, updateFields);

      return { success: true, needsReapproval };
    } catch (err) {
      console.error('Error updating equipment:', err);
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  // Change Equipment Status (สำหรับอุปกรณ์ชิ้นใหญ่ - ใช้ภายในทีม)
  const changeEquipmentStatus = async (equipmentId, newStatus, usageInfo = {}) => {
    try {
      if (!currentUser || !currentTeam) {
        throw new Error('กรุณาเข้าสู่ระบบและเลือกทีม');
      }

      const equipmentRef = doc(db, 'equipment', equipmentId);
      const equipmentSnap = await getDoc(equipmentRef);

      if (!equipmentSnap.exists()) {
        throw new Error('ไม่พบอุปกรณ์');
      }

      const equipmentData = equipmentSnap.data();

      // ตรวจสอบว่าเป็นอุปกรณ์ชิ้นใหญ่
      if (equipmentData.type !== EQUIPMENT_TYPES.LARGE_ITEM) {
        throw new Error('ฟีเจอร์นี้ใช้ได้เฉพาะอุปกรณ์ชิ้นใหญ่');
      }

      // ตรวจสอบว่า Status ที่เปลี่ยนถูกต้องหรือไม่
      const allowedStatuses = [EQUIPMENT_STATUS.AVAILABLE, EQUIPMENT_STATUS.IN_USE];
      if (!allowedStatuses.includes(newStatus)) {
        throw new Error('Status ที่เลือกไม่ถูกต้อง');
      }

      // ตรวจสอบว่า Status ปัจจุบันสามารถเปลี่ยนได้หรือไม่
      if (equipmentData.status === EQUIPMENT_STATUS.BORROWED) {
        throw new Error('ไม่สามารถเปลี่ยน Status ได้ เพราะอุปกรณ์ถูกยืมอยู่');
      }

      if (equipmentData.status === EQUIPMENT_STATUS.REPAIR) {
        throw new Error('ไม่สามารถเปลี่ยน Status ได้ เพราะอุปกรณ์อยู่ระหว่างส่งซ่อม');
      }

      const updateFields = {
        status: newStatus,
        updatedAt: serverTimestamp()
      };

      // ถ้าเปลี่ยนเป็น IN_USE → บันทึกข้อมูลการใช้งาน
      if (newStatus === EQUIPMENT_STATUS.IN_USE) {
        updateFields.inUseBy = currentUser.uid;
        updateFields.inUseByName = currentUser.displayName || currentUser.email;
        updateFields.inUseAt = serverTimestamp();
        updateFields.inUseLocation = usageInfo.location || equipmentData.currentLocation || '';
        updateFields.inUsePatientBed = usageInfo.patientBed || '';
        updateFields.inUseNotes = usageInfo.notes || '';
      }

      // ถ้าเปลี่ยนเป็น AVAILABLE → ลบข้อมูลการใช้งาน
      if (newStatus === EQUIPMENT_STATUS.AVAILABLE) {
        updateFields.inUseBy = null;
        updateFields.inUseByName = null;
        updateFields.inUseAt = null;
        updateFields.inUseLocation = null;
        updateFields.inUsePatientBed = null;
        updateFields.inUseNotes = null;
      }

      await updateDoc(equipmentRef, updateFields);

      return { success: true };
    } catch (err) {
      console.error('Error changing equipment status:', err);
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  // Delete Equipment (ลบได้เลยถ้าไม่มี Stock/History)
  const deleteEquipment = async (equipmentId) => {
    try {
      if (!currentUser || !currentTeam) {
        throw new Error('กรุณาเข้าสู่ระบบและเลือกทีม');
      }

      const equipmentRef = doc(db, 'equipment', equipmentId);
      const equipmentSnap = await getDoc(equipmentRef);

      if (!equipmentSnap.exists()) {
        throw new Error('ไม่พบอุปกรณ์');
      }

      const equipmentData = equipmentSnap.data();

      // ตรวจสอบว่าเป็นเจ้าของหรือ Leader/Deputy Leader
      const userTeamRole = currentTeam.userRole;
      const isOwner = equipmentData.createdBy === currentUser.uid;
      const isLeader = userTeamRole === 'LEADER' || userTeamRole === 'DEPUTY_LEADER';

      if (!isOwner && !isLeader) {
        throw new Error('คุณไม่มีสิทธิ์ลบอุปกรณ์นี้');
      }

      // ตรวจสอบเงื่อนไข: มี Stock หรือ History หรือไม่?
      if (equipmentData.currentStock > 0) {
        throw new Error('ไม่สามารถลบอุปกรณ์ที่มี Stock อยู่ได้ กรุณา Stock Out ให้หมดก่อน');
      }

      // ตรวจสอบ History
      const hasStockHistory = await getDocs(query(
        collection(db, 'equipmentStockTransactions'),
        where('equipmentId', '==', equipmentId),
        limit(1)
      ));

      if (!hasStockHistory.empty) {
        throw new Error('ไม่สามารถลบอุปกรณ์ที่มีประวัติ Stock ได้');
      }

      const hasInspectionHistory = await getDocs(query(
        collection(db, 'equipmentInspections'),
        where('items', 'array-contains-any', [{ equipmentId: equipmentId }]),
        limit(1)
      ));

      if (!hasInspectionHistory.empty) {
        throw new Error('ไม่สามารถลบอุปกรณ์ที่มีประวัติการตรวจสอบได้');
      }

      // ลบอุปกรณ์
      await deleteDoc(equipmentRef);

      return { success: true };
    } catch (err) {
      console.error('Error deleting equipment:', err);
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  // Reject Stock In
  const rejectStockIn = async (transactionId, rejectionReason) => {
    try {
      if (!currentUser || !currentTeam) {
        throw new Error('กรุณาเข้าสู่ระบบและเลือกทีม');
      }

      // Check if user is Leader or Deputy Leader
      const userTeamRole = currentTeam.userRole;
      if (userTeamRole !== 'LEADER' && userTeamRole !== 'DEPUTY_LEADER') {
        throw new Error('คุณไม่มีสิทธิ์ไม่อนุมัติ');
      }

      const transactionRef = doc(db, 'equipmentStockTransactions', transactionId);
      const transactionSnap = await getDoc(transactionRef);

      if (!transactionSnap.exists()) {
        throw new Error('ไม่พบรายการ');
      }

      const transactionData = transactionSnap.data();

      // If transaction was already approved, subtract stock
      if (transactionData.status === 'APPROVED') {
        const equipmentRef = doc(db, 'equipment', transactionData.equipmentId);
        const equipmentSnap = await getDoc(equipmentRef);

        if (equipmentSnap.exists()) {
          const equipmentData = equipmentSnap.data();
          // Subtract stock that was previously added
          const newStock = Math.max(0, (equipmentData.currentStock || 0) - transactionData.quantity);
          await updateDoc(equipmentRef, {
            currentStock: newStock,
            updatedAt: serverTimestamp()
          });
        }
      }

      // Update transaction status
      await updateDoc(transactionRef, {
        status: 'REJECTED',
        rejectedBy: currentUser.uid,
        rejectedByName: currentUser.displayName || currentUser.email,
        rejectionReason: rejectionReason || '',
        rejectedAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      return { success: true };
    } catch (err) {
      console.error('Error rejecting stock in:', err);
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  // Helper: Get team leaders (LEADER and DEPUTY_LEADER)
  const getTeamLeaders = async (teamId) => {
    try {
      const teamRef = doc(db, 'teams', teamId);
      const teamSnap = await getDoc(teamRef);
      
      if (!teamSnap.exists()) {
        return [];
      }
      
      const teamData = teamSnap.data();
      const leaders = [];
      
      // Add main leader
      if (teamData.leaderId) {
        leaders.push(teamData.leaderId);
      }
      
      // Add deputy leaders
      if (teamData.deputyLeaders && Array.isArray(teamData.deputyLeaders)) {
        teamData.deputyLeaders.forEach(deputy => {
          if (deputy.uid && !leaders.includes(deputy.uid)) {
            leaders.push(deputy.uid);
          }
        });
      }
      
      return leaders;
    } catch (err) {
      console.error('Error getting team leaders:', err);
      return [];
    }
  };

  // Create Inspection (Draft mode by default)
  const createInspection = async (inspectionData) => {
    try {
      if (!currentUser || !currentTeam) {
        throw new Error('กรุณาเข้าสู่ระบบและเลือกทีม');
      }

      const inspection = {
        teamId: currentTeam.id,
        date: inspectionData.date || serverTimestamp(),
        round: inspectionData.round,
        inspectedBy: currentUser.uid,
        inspectedByName: currentUser.displayName || currentUser.email,
        inspectedAt: serverTimestamp(),
        shiftType: inspectionData.shiftType,
        items: inspectionData.items || [],
        totalItems: inspectionData.items?.length || 0,
        totalAvailable: inspectionData.items?.reduce((sum, item) => sum + (item.availableQuantity || 0), 0) || 0,
        totalDamaged: inspectionData.items?.reduce((sum, item) => sum + (item.damagedQuantity || 0), 0) || 0,
        totalLost: inspectionData.items?.reduce((sum, item) => sum + (item.lostQuantity || 0), 0) || 0,
        notes: inspectionData.notes || '',
        status: inspectionData.status || 'DRAFT', // DRAFT, PENDING_APPROVAL, APPROVED, REJECTED
        submittedAt: null,
        approvedBy: null,
        approvedByName: null,
        approvedAt: null,
        rejectedBy: null,
        rejectedByName: null,
        rejectedAt: null,
        rejectionReason: null,
        version: 1,
        previousVersionId: null,
        editedAfterSubmission: false,
        lastEditedAt: null,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      const inspectionRef = await addDoc(collection(db, 'equipmentInspections'), inspection);

      return { success: true, inspectionId: inspectionRef.id };
    } catch (err) {
      console.error('Error creating inspection:', err);
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  // Submit Inspection (Change status to PENDING_APPROVAL)
  const submitInspection = async (inspectionId) => {
    try {
      if (!currentUser || !currentTeam) {
        throw new Error('กรุณาเข้าสู่ระบบและเลือกทีม');
      }

      const inspectionRef = doc(db, 'equipmentInspections', inspectionId);
      const inspectionSnap = await getDoc(inspectionRef);

      if (!inspectionSnap.exists()) {
        throw new Error('ไม่พบรายการตรวจสอบ');
      }

      const inspectionData = inspectionSnap.data();

      // Only allow submit if status is DRAFT
      if (inspectionData.status !== 'DRAFT') {
        throw new Error('สามารถส่งได้เฉพาะรายการที่ยังเป็น Draft');
      }

      // Check if user is the inspector
      if (inspectionData.inspectedBy !== currentUser.uid) {
        throw new Error('คุณไม่มีสิทธิ์ส่งรายการนี้');
      }

      // Update status to PENDING_APPROVAL
      await updateDoc(inspectionRef, {
        status: 'PENDING_APPROVAL',
        submittedAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      // Helper function to get round label
      const getRoundLabel = (round) => {
        const rounds = {
          [INSPECTION_ROUNDS.MORNING_BEFORE]: 'เช้า - ก่อนเข้าเวร',
          [INSPECTION_ROUNDS.MORNING_AFTER]: 'เช้า - หลังเข้าเวร',
          [INSPECTION_ROUNDS.AFTERNOON_BEFORE]: 'บ่าย - ก่อนเข้าเวร',
          [INSPECTION_ROUNDS.AFTERNOON_AFTER]: 'บ่าย - หลังเข้าเวร',
          [INSPECTION_ROUNDS.NIGHT_BEFORE]: 'ดึก - ก่อนเข้าเวร',
          [INSPECTION_ROUNDS.NIGHT_AFTER]: 'ดึก - หลังเข้าเวร'
        };
        return rounds[round] || round;
      };

      // Send notification to team leaders
      const leaders = await getTeamLeaders(currentTeam.id);
      for (const leaderId of leaders) {
        try {
          const roundLabel = getRoundLabel(inspectionData.round);
          const notificationsRef = collection(db, notificationModel.collection);
          const notification = notificationModel.createNotification(
            leaderId,
            notificationModel.types.INSPECTION_SUBMITTED,
            '📋 มีการส่งผลการตรวจสอบใหม่',
            `${inspectionData.inspectedByName} ส่งผลการตรวจสอบรอบ${roundLabel}`,
            {
              inspectionId: inspectionId,
              teamId: currentTeam.id,
              round: inspectionData.round,
              date: inspectionData.date
            }
          );
          await addDoc(notificationsRef, {
            ...notification,
            read: false,
            readAt: null,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp()
          });
        } catch (notifErr) {
          console.error('Error sending notification to leader:', notifErr);
        }
      }

      return { success: true };
    } catch (err) {
      console.error('Error submitting inspection:', err);
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  // Update Inspection (Edit after submission)
  const updateInspection = async (inspectionId, updateData) => {
    try {
      if (!currentUser || !currentTeam) {
        throw new Error('กรุณาเข้าสู่ระบบและเลือกทีม');
      }

      const inspectionRef = doc(db, 'equipmentInspections', inspectionId);
      const inspectionSnap = await getDoc(inspectionRef);

      if (!inspectionSnap.exists()) {
        throw new Error('ไม่พบรายการตรวจสอบ');
      }

      const inspectionData = inspectionSnap.data();

      // Only allow update if status is DRAFT or PENDING_APPROVAL
      if (inspectionData.status !== 'DRAFT' && inspectionData.status !== 'PENDING_APPROVAL') {
        throw new Error('ไม่สามารถแก้ไขรายการที่ได้รับการอนุมัติแล้ว');
      }

      // Check if user is the inspector
      if (inspectionData.inspectedBy !== currentUser.uid) {
        throw new Error('คุณไม่มีสิทธิ์แก้ไขรายการนี้');
      }

      // If updating from PENDING_APPROVAL, change back to DRAFT
      const newStatus = inspectionData.status === 'PENDING_APPROVAL' ? 'DRAFT' : inspectionData.status;
      const editedAfterSubmission = inspectionData.status === 'PENDING_APPROVAL';

      // Calculate totals
      const items = updateData.items || inspectionData.items || [];
      const totalItems = items.length;
      const totalAvailable = items.reduce((sum, item) => sum + (item.availableQuantity || 0), 0);
      const totalDamaged = items.reduce((sum, item) => sum + (item.damagedQuantity || 0), 0);
      const totalLost = items.reduce((sum, item) => sum + (item.lostQuantity || 0), 0);

      await updateDoc(inspectionRef, {
        ...updateData,
        totalItems,
        totalAvailable,
        totalDamaged,
        totalLost,
        status: newStatus,
        editedAfterSubmission: editedAfterSubmission || inspectionData.editedAfterSubmission,
        lastEditedAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      // If edited after submission, send notification to leaders
      if (editedAfterSubmission) {
        // Helper function to get round label
        const getRoundLabel = (round) => {
          const rounds = {
            [INSPECTION_ROUNDS.MORNING_BEFORE]: 'เช้า - ก่อนเข้าเวร',
            [INSPECTION_ROUNDS.MORNING_AFTER]: 'เช้า - หลังเข้าเวร',
            [INSPECTION_ROUNDS.AFTERNOON_BEFORE]: 'บ่าย - ก่อนเข้าเวร',
            [INSPECTION_ROUNDS.AFTERNOON_AFTER]: 'บ่าย - หลังเข้าเวร',
            [INSPECTION_ROUNDS.NIGHT_BEFORE]: 'ดึก - ก่อนเข้าเวร',
            [INSPECTION_ROUNDS.NIGHT_AFTER]: 'ดึก - หลังเข้าเวร'
          };
          return rounds[round] || round;
        };

        const leaders = await getTeamLeaders(currentTeam.id);
        for (const leaderId of leaders) {
          try {
            const roundLabel = getRoundLabel(inspectionData.round);
            const notificationsRef = collection(db, notificationModel.collection);
            const notification = notificationModel.createNotification(
              leaderId,
              notificationModel.types.INSPECTION_UPDATED,
              '✏️ มีการแก้ไขผลการตรวจสอบ',
              `${inspectionData.inspectedByName} แก้ไขผลการตรวจสอบรอบ${roundLabel}`,
              {
                inspectionId: inspectionId,
                teamId: currentTeam.id,
                round: inspectionData.round,
                date: inspectionData.date
              }
            );
            await addDoc(notificationsRef, {
              ...notification,
              read: false,
              readAt: null,
              createdAt: serverTimestamp(),
              updatedAt: serverTimestamp()
            });
          } catch (notifErr) {
            console.error('Error sending notification to leader:', notifErr);
          }
        }
      }

      return { success: true };
    } catch (err) {
      console.error('Error updating inspection:', err);
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  // Approve Inspection (Leader only)
  const approveInspection = async (inspectionId) => {
    try {
      if (!currentUser || !currentTeam) {
        throw new Error('กรุณาเข้าสู่ระบบและเลือกทีม');
      }

      // Check if user is Leader or Deputy Leader
      const userTeamRole = currentTeam.userRole;
      if (userTeamRole !== 'LEADER' && userTeamRole !== 'DEPUTY_LEADER') {
        throw new Error('คุณไม่มีสิทธิ์อนุมัติ');
      }

      const inspectionRef = doc(db, 'equipmentInspections', inspectionId);
      const inspectionSnap = await getDoc(inspectionRef);

      if (!inspectionSnap.exists()) {
        throw new Error('ไม่พบรายการตรวจสอบ');
      }

      const inspectionData = inspectionSnap.data();

      // Only allow approve if status is PENDING_APPROVAL
      if (inspectionData.status !== 'PENDING_APPROVAL') {
        throw new Error('รายการนี้ไม่อยู่ในสถานะที่สามารถอนุมัติได้');
      }

      // Update status to APPROVED
      await updateDoc(inspectionRef, {
        status: 'APPROVED',
        approvedBy: currentUser.uid,
        approvedByName: currentUser.displayName || currentUser.email,
        approvedAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      // Update equipment currentStock based on inspection results (for small items)
      for (const item of inspectionData.items || []) {
        if (item.type === EQUIPMENT_TYPES.SMALL_ITEM) {
          const equipmentRef = doc(db, 'equipment', item.equipmentId);
          const equipmentSnap = await getDoc(equipmentRef);
          
          if (equipmentSnap.exists()) {
            // Update currentStock to availableQuantity from inspection
            await updateDoc(equipmentRef, {
              currentStock: item.availableQuantity || 0,
              updatedAt: serverTimestamp()
            });
          }
        }
      }

      // Send notification to inspector
      try {
        const notificationsRef = collection(db, notificationModel.collection);
        const notification = notificationModel.createNotification(
          inspectionData.inspectedBy,
          notificationModel.types.INSPECTION_APPROVED,
          '✅ ผลการตรวจสอบได้รับการอนุมัติ',
          `${currentUser.displayName || currentUser.email} อนุมัติผลการตรวจสอบของคุณ`,
          {
            inspectionId: inspectionId,
            teamId: currentTeam.id,
            round: inspectionData.round,
            date: inspectionData.date
          }
        );
        await addDoc(notificationsRef, {
          ...notification,
          read: false,
          readAt: null,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
      } catch (notifErr) {
        console.error('Error sending notification to inspector:', notifErr);
      }

      return { success: true };
    } catch (err) {
      console.error('Error approving inspection:', err);
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  // Reject Inspection (Leader only)
  const rejectInspection = async (inspectionId, rejectionReason) => {
    try {
      if (!currentUser || !currentTeam) {
        throw new Error('กรุณาเข้าสู่ระบบและเลือกทีม');
      }

      // Check if user is Leader or Deputy Leader
      const userTeamRole = currentTeam.userRole;
      if (userTeamRole !== 'LEADER' && userTeamRole !== 'DEPUTY_LEADER') {
        throw new Error('คุณไม่มีสิทธิ์ไม่อนุมัติ');
      }

      const inspectionRef = doc(db, 'equipmentInspections', inspectionId);
      const inspectionSnap = await getDoc(inspectionRef);

      if (!inspectionSnap.exists()) {
        throw new Error('ไม่พบรายการตรวจสอบ');
      }

      const inspectionData = inspectionSnap.data();

      // Only allow reject if status is PENDING_APPROVAL
      if (inspectionData.status !== 'PENDING_APPROVAL') {
        throw new Error('รายการนี้ไม่อยู่ในสถานะที่สามารถไม่อนุมัติได้');
      }

      // Update status to REJECTED
      await updateDoc(inspectionRef, {
        status: 'REJECTED',
        rejectedBy: currentUser.uid,
        rejectedByName: currentUser.displayName || currentUser.email,
        rejectedAt: serverTimestamp(),
        rejectionReason: rejectionReason || '',
        updatedAt: serverTimestamp()
      });

      // Send notification to inspector
      try {
        const notificationsRef = collection(db, notificationModel.collection);
        const notification = notificationModel.createNotification(
          inspectionData.inspectedBy,
          notificationModel.types.INSPECTION_REJECTED,
          '❌ ผลการตรวจสอบถูกปฏิเสธ',
          `${currentUser.displayName || currentUser.email} ปฏิเสธผลการตรวจสอบของคุณ${rejectionReason ? `: ${rejectionReason}` : ''}`,
          {
            inspectionId: inspectionId,
            teamId: currentTeam.id,
            round: inspectionData.round,
            date: inspectionData.date,
            rejectionReason: rejectionReason || ''
          }
        );
        await addDoc(notificationsRef, {
          ...notification,
          read: false,
          readAt: null,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
      } catch (notifErr) {
        console.error('Error sending notification to inspector:', notifErr);
      }

      return { success: true };
    } catch (err) {
      console.error('Error rejecting inspection:', err);
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  // Borrow Equipment
  const borrowEquipment = async (equipmentId, borrowData) => {
    try {
      if (!currentUser || !currentTeam) {
        throw new Error('กรุณาเข้าสู่ระบบและเลือกทีม');
      }

      const equipmentRef = doc(db, 'equipment', equipmentId);
      const equipmentSnap = await getDoc(equipmentRef);

      if (!equipmentSnap.exists()) {
        throw new Error('ไม่พบอุปกรณ์');
      }

      const equipmentData = equipmentSnap.data();

      if (equipmentData.type !== EQUIPMENT_TYPES.LARGE_ITEM) {
        throw new Error('สามารถยืมได้เฉพาะอุปกรณ์ชิ้นใหญ่');
      }

      // Create borrow request
      const borrowRequest = {
        teamId: currentTeam.id,
        equipmentId: equipmentId,
        type: 'BORROW',
        borrowedBy: currentUser.uid,
        borrowedByName: currentUser.displayName || currentUser.email,
        expectedReturnAt: borrowData.expectedReturnAt,
        notes: borrowData.notes || '',
        status: 'PENDING',
        createdAt: serverTimestamp()
      };

      const borrowRef = await addDoc(collection(db, 'equipmentBorrows'), borrowRequest);

      // Send notification to team leaders
      try {
        const leaders = await getTeamLeaders(currentTeam.id);
        for (const leaderId of leaders) {
          try {
            const notificationsRef = collection(db, notificationModel.collection);
            const notification = notificationModel.createNotification(
              leaderId,
              notificationModel.types.EQUIPMENT_BORROW_REQUESTED || 'equipment_borrow_requested',
              '📦 มีคำขอยืมอุปกรณ์ใหม่',
              `${currentUser.displayName || currentUser.email} ส่งคำขอยืม "${equipmentData.name}"`,
              {
                borrowId: borrowRef.id,
                equipmentId: equipmentId,
                teamId: currentTeam.id
              }
            );
            await addDoc(notificationsRef, {
              ...notification,
              read: false,
              readAt: null,
              createdAt: serverTimestamp(),
              updatedAt: serverTimestamp()
            });
          } catch (notifErr) {
            console.error('Error sending notification to leader:', notifErr);
          }
        }
      } catch (notifErr) {
        console.error('Error getting team leaders:', notifErr);
      }

      return { success: true, borrowId: borrowRef.id };
    } catch (err) {
      console.error('Error borrowing equipment:', err);
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  // Approve Borrow
  const approveBorrow = async (borrowId) => {
    try {
      if (!currentUser || !currentTeam) {
        throw new Error('กรุณาเข้าสู่ระบบและเลือกทีม');
      }

      const userTeamRole = currentTeam.userRole;
      if (userTeamRole !== 'LEADER' && userTeamRole !== 'DEPUTY_LEADER') {
        throw new Error('คุณไม่มีสิทธิ์อนุมัติ');
      }

      const borrowRef = doc(db, 'equipmentBorrows', borrowId);
      const borrowSnap = await getDoc(borrowRef);

      if (!borrowSnap.exists()) {
        throw new Error('ไม่พบคำขอ');
      }

      const borrowData = borrowSnap.data();
      
      // Check if already approved or rejected
      if (borrowData.status === 'APPROVED' || borrowData.status === 'REJECTED') {
        throw new Error(`คำขอนี้ได้รับการ${borrowData.status === 'APPROVED' ? 'อนุมัติ' : 'ปฏิเสธ'}แล้ว`);
      }

      const equipmentRef = doc(db, 'equipment', borrowData.equipmentId);
      const equipmentSnap = await getDoc(equipmentRef);

      if (!equipmentSnap.exists()) {
        throw new Error('ไม่พบอุปกรณ์');
      }

      const equipmentData = equipmentSnap.data();

      // Check if equipment is already borrowed
      if (equipmentData.status === EQUIPMENT_STATUS.BORROWED) {
        throw new Error('อุปกรณ์นี้ถูกยืมไปแล้ว');
      }

      // Update equipment status
      await updateDoc(equipmentRef, {
        status: EQUIPMENT_STATUS.BORROWED,
        borrowedBy: borrowData.borrowedBy,
        borrowedByName: borrowData.borrowedByName,
        borrowedAt: serverTimestamp(),
        expectedReturnAt: borrowData.expectedReturnAt,
        updatedAt: serverTimestamp()
      });

      // Update borrow request
      await updateDoc(borrowRef, {
        status: 'APPROVED',
        approvedBy: currentUser.uid,
        approvedByName: currentUser.displayName || currentUser.email,
        approvedAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      // Send notification to borrower
      try {
        const notificationsRef = collection(db, notificationModel.collection);
        const notification = notificationModel.createNotification(
          borrowData.borrowedBy,
          notificationModel.types.EQUIPMENT_BORROW_APPROVED || 'equipment_borrow_approved',
          '✅ คำขอยืมอุปกรณ์ได้รับการอนุมัติ',
          `คำขอยืมอุปกรณ์ "${equipmentData.name}" ได้รับการอนุมัติแล้ว`,
          {
            borrowId: borrowId,
            equipmentId: borrowData.equipmentId,
            teamId: currentTeam.id
          }
        );
        await addDoc(notificationsRef, {
          ...notification,
          read: false,
          readAt: null,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
      } catch (notifErr) {
        console.error('Error sending notification:', notifErr);
      }

      return { success: true };
    } catch (err) {
      console.error('Error approving borrow:', err);
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  // Reject Borrow
  const rejectBorrow = async (borrowId, rejectionReason = '') => {
    try {
      if (!currentUser || !currentTeam) {
        throw new Error('กรุณาเข้าสู่ระบบและเลือกทีม');
      }

      const userTeamRole = currentTeam.userRole;
      if (userTeamRole !== 'LEADER' && userTeamRole !== 'DEPUTY_LEADER') {
        throw new Error('คุณไม่มีสิทธิ์ปฏิเสธ');
      }

      const borrowRef = doc(db, 'equipmentBorrows', borrowId);
      const borrowSnap = await getDoc(borrowRef);

      if (!borrowSnap.exists()) {
        throw new Error('ไม่พบคำขอ');
      }

      const borrowData = borrowSnap.data();

      // Check if already approved or rejected
      if (borrowData.status === 'APPROVED' || borrowData.status === 'REJECTED') {
        throw new Error(`คำขอนี้ได้รับการ${borrowData.status === 'APPROVED' ? 'อนุมัติ' : 'ปฏิเสธ'}แล้ว`);
      }

      // Update borrow request
      await updateDoc(borrowRef, {
        status: 'REJECTED',
        rejectedBy: currentUser.uid,
        rejectedByName: currentUser.displayName || currentUser.email,
        rejectedAt: serverTimestamp(),
        rejectionReason: rejectionReason || '',
        updatedAt: serverTimestamp()
      });

      // Send notification to borrower
      try {
        const equipmentRef = doc(db, 'equipment', borrowData.equipmentId);
        const equipmentSnap = await getDoc(equipmentRef);
        const equipmentData = equipmentSnap.exists() ? equipmentSnap.data() : { name: 'อุปกรณ์' };

        const notificationsRef = collection(db, notificationModel.collection);
        const notification = notificationModel.createNotification(
          borrowData.borrowedBy,
          notificationModel.types.EQUIPMENT_BORROW_REJECTED || 'equipment_borrow_rejected',
          '❌ คำขอยืมอุปกรณ์ถูกปฏิเสธ',
          `คำขอยืมอุปกรณ์ "${equipmentData.name}" ถูกปฏิเสธ${rejectionReason ? `: ${rejectionReason}` : ''}`,
          {
            borrowId: borrowId,
            equipmentId: borrowData.equipmentId,
            teamId: currentTeam.id,
            rejectionReason: rejectionReason || ''
          }
        );
        await addDoc(notificationsRef, {
          ...notification,
          read: false,
          readAt: null,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
      } catch (notifErr) {
        console.error('Error sending notification:', notifErr);
      }

      return { success: true };
    } catch (err) {
      console.error('Error rejecting borrow:', err);
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  // Return Equipment (Request for approval)
  const returnEquipment = async (equipmentId, returnData, borrowId = null) => {
    try {
      if (!currentUser || !currentTeam) {
        throw new Error('กรุณาเข้าสู่ระบบและเลือกทีม');
      }

      const equipmentRef = doc(db, 'equipment', equipmentId);
      const equipmentSnap = await getDoc(equipmentRef);

      if (!equipmentSnap.exists()) {
        throw new Error('ไม่พบอุปกรณ์');
      }

      const equipmentData = equipmentSnap.data();

      let activeBorrowId = borrowId;

      // If borrowId is not provided, find active borrow record
      if (!activeBorrowId) {
        const borrowsQuery = query(
          collection(db, 'equipmentBorrows'),
          where('equipmentId', '==', equipmentId),
          where('borrowedBy', '==', currentUser.uid),
          where('type', '==', 'BORROW'),
          where('status', '==', 'APPROVED'),
          orderBy('createdAt', 'desc')
        );

        let borrowsSnapshot;
        try {
          borrowsSnapshot = await getDocs(borrowsQuery);
        } catch (queryErr) {
          // Fallback: query without orderBy if index is missing
          console.warn('Query with orderBy failed, trying without orderBy:', queryErr);
          const fallbackQuery = query(
            collection(db, 'equipmentBorrows'),
            where('equipmentId', '==', equipmentId),
            where('borrowedBy', '==', currentUser.uid),
            where('type', '==', 'BORROW'),
            where('status', '==', 'APPROVED')
          );
          borrowsSnapshot = await getDocs(fallbackQuery);
        }

        // Check if user has an active borrow record
        if (borrowsSnapshot.empty) {
          throw new Error('คุณไม่ได้ยืมอุปกรณ์นี้ หรือรายการยืมยังไม่ได้รับการอนุมัติ');
        }

        activeBorrowId = borrowsSnapshot.docs[0].id;
      } else {
        // Verify that the provided borrowId belongs to current user
        const borrowRef = doc(db, 'equipmentBorrows', activeBorrowId);
        const borrowSnap = await getDoc(borrowRef);
        
        if (!borrowSnap.exists()) {
          throw new Error('ไม่พบรายการยืม');
        }

        const borrowData = borrowSnap.data();
        if (borrowData.borrowedBy !== currentUser.uid || 
            borrowData.equipmentId !== equipmentId ||
            borrowData.status !== 'APPROVED' ||
            borrowData.type !== 'BORROW') {
          throw new Error('คุณไม่มีสิทธิ์คืนอุปกรณ์นี้');
        }
      }

      // Create return request (PENDING approval)
      const returnRecord = {
        teamId: currentTeam.id,
        equipmentId: equipmentId,
        equipmentName: equipmentData.name,
        equipmentCode: equipmentData.code,
        type: 'RETURN',
        borrowId: activeBorrowId, // Reference to original borrow
        returnedBy: currentUser.uid,
        returnedByName: currentUser.displayName || currentUser.email,
        returnCondition: returnData.condition || 'GOOD',
        returnDescription: returnData.notes || '',
        images: returnData.images || [],
        status: 'PENDING', // PENDING approval from leader
        actualReturnDate: serverTimestamp(),
        approvedBy: null,
        approvedByName: null,
        approvedAt: null,
        rejectedBy: null,
        rejectedByName: null,
        rejectedAt: null,
        rejectionReason: null,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      const returnRef = await addDoc(collection(db, 'equipmentBorrows'), returnRecord);

      // Send notification to team leaders
      try {
        const leaders = await getTeamLeaders(currentTeam.id);
        for (const leaderId of leaders) {
          try {
            const notificationsRef = collection(db, notificationModel.collection);
            const notification = notificationModel.createNotification(
              leaderId,
              notificationModel.types.EQUIPMENT_RETURN_REQUESTED || 'equipment_return_requested',
              '📥 มีคำขอคืนอุปกรณ์',
              `${currentUser.displayName || currentUser.email} ส่งคำขอคืน "${equipmentData.name}"`,
              {
                returnId: returnRef.id,
                equipmentId: equipmentId,
                teamId: currentTeam.id,
                borrowId: activeBorrowId
              }
            );
            await addDoc(notificationsRef, {
              ...notification,
              read: false,
              readAt: null,
              createdAt: serverTimestamp(),
              updatedAt: serverTimestamp()
            });
          } catch (notifErr) {
            console.error('Error sending notification to leader:', notifErr);
          }
        }
      } catch (notifErr) {
        console.error('Error getting team leaders:', notifErr);
      }

      return { success: true, returnId: returnRef.id };
    } catch (err) {
      console.error('Error returning equipment:', err);
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  // Approve Return
  const approveReturn = async (returnId) => {
    try {
      if (!currentUser || !currentTeam) {
        throw new Error('กรุณาเข้าสู่ระบบและเลือกทีม');
      }

      const userTeamRole = currentTeam.userRole;
      if (userTeamRole !== 'LEADER' && userTeamRole !== 'DEPUTY_LEADER') {
        throw new Error('คุณไม่มีสิทธิ์อนุมัติ');
      }

      const returnRef = doc(db, 'equipmentBorrows', returnId);
      const returnSnap = await getDoc(returnRef);

      if (!returnSnap.exists()) {
        throw new Error('ไม่พบคำขอ');
      }

      const returnData = returnSnap.data();

      if (returnData.type !== 'RETURN') {
        throw new Error('ไม่ใช่คำขอคืนอุปกรณ์');
      }

      if (returnData.status === 'APPROVED' || returnData.status === 'REJECTED') {
        throw new Error(`คำขอนี้ได้รับการ${returnData.status === 'APPROVED' ? 'อนุมัติ' : 'ปฏิเสธ'}แล้ว`);
      }

      const equipmentRef = doc(db, 'equipment', returnData.equipmentId);
      const equipmentSnap = await getDoc(equipmentRef);

      if (!equipmentSnap.exists()) {
        throw new Error('ไม่พบอุปกรณ์');
      }

      const equipmentData = equipmentSnap.data();

      // Determine new equipment status based on return condition
      let newStatus = EQUIPMENT_STATUS.AVAILABLE;
      if (returnData.returnCondition === 'DAMAGED') {
        newStatus = EQUIPMENT_STATUS.DAMAGED;
      } else if (returnData.returnCondition === 'NEEDS_REPAIR') {
        newStatus = EQUIPMENT_STATUS.REPAIR;
      }

      // Update equipment status to AVAILABLE (or DAMAGED/REPAIR if needed)
      await updateDoc(equipmentRef, {
        status: newStatus,
        borrowedBy: null,
        borrowedByName: null,
        borrowedAt: null,
        expectedReturnAt: null,
        updatedAt: serverTimestamp()
      });

      // Update original borrow record if exists
      if (returnData.borrowId) {
        try {
          const borrowRef = doc(db, 'equipmentBorrows', returnData.borrowId);
          await updateDoc(borrowRef, {
            status: 'RETURNED',
            actualReturnDate: serverTimestamp(),
            returnCondition: returnData.returnCondition,
            updatedAt: serverTimestamp()
          });
        } catch (borrowErr) {
          console.error('Error updating borrow record:', borrowErr);
          // Continue even if this fails
        }
      }

      // Update return request status
      await updateDoc(returnRef, {
        status: 'APPROVED',
        approvedBy: currentUser.uid,
        approvedByName: currentUser.displayName || currentUser.email,
        approvedAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      // Send notification to borrower
      try {
        const notificationsRef = collection(db, notificationModel.collection);
        const notification = notificationModel.createNotification(
          returnData.returnedBy,
          notificationModel.types.EQUIPMENT_RETURN_APPROVED || 'equipment_return_approved',
          '✅ คำขอคืนอุปกรณ์ได้รับการอนุมัติ',
          `คำขอคืน "${equipmentData.name}" ได้รับการอนุมัติแล้ว`,
          {
            returnId: returnId,
            equipmentId: returnData.equipmentId,
            teamId: currentTeam.id
          }
        );
        await addDoc(notificationsRef, {
          ...notification,
          read: false,
          readAt: null,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
      } catch (notifErr) {
        console.error('Error sending notification:', notifErr);
      }

      return { success: true };
    } catch (err) {
      console.error('Error approving return:', err);
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  // Reject Return
  const rejectReturn = async (returnId, rejectionReason = '') => {
    try {
      if (!currentUser || !currentTeam) {
        throw new Error('กรุณาเข้าสู่ระบบและเลือกทีม');
      }

      const userTeamRole = currentTeam.userRole;
      if (userTeamRole !== 'LEADER' && userTeamRole !== 'DEPUTY_LEADER') {
        throw new Error('คุณไม่มีสิทธิ์ปฏิเสธ');
      }

      const returnRef = doc(db, 'equipmentBorrows', returnId);
      const returnSnap = await getDoc(returnRef);

      if (!returnSnap.exists()) {
        throw new Error('ไม่พบคำขอ');
      }

      const returnData = returnSnap.data();

      if (returnData.type !== 'RETURN') {
        throw new Error('ไม่ใช่คำขอคืนอุปกรณ์');
      }

      if (returnData.status === 'APPROVED' || returnData.status === 'REJECTED') {
        throw new Error(`คำขอนี้ได้รับการ${returnData.status === 'APPROVED' ? 'อนุมัติ' : 'ปฏิเสธ'}แล้ว`);
      }

      // Update return request status
      await updateDoc(returnRef, {
        status: 'REJECTED',
        rejectedBy: currentUser.uid,
        rejectedByName: currentUser.displayName || currentUser.email,
        rejectedAt: serverTimestamp(),
        rejectionReason: rejectionReason || '',
        updatedAt: serverTimestamp()
      });

      // Send notification to borrower
      try {
        const equipmentRef = doc(db, 'equipment', returnData.equipmentId);
        const equipmentSnap = await getDoc(equipmentRef);
        const equipmentData = equipmentSnap.exists() ? equipmentSnap.data() : { name: 'อุปกรณ์' };

        const notificationsRef = collection(db, notificationModel.collection);
        const notification = notificationModel.createNotification(
          returnData.returnedBy,
          notificationModel.types.EQUIPMENT_RETURN_REJECTED || 'equipment_return_rejected',
          '❌ คำขอคืนอุปกรณ์ถูกปฏิเสธ',
          `คำขอคืน "${equipmentData.name}" ถูกปฏิเสธ${rejectionReason ? `: ${rejectionReason}` : ''}`,
          {
            returnId: returnId,
            equipmentId: returnData.equipmentId,
            teamId: currentTeam.id,
            rejectionReason: rejectionReason || ''
          }
        );
        await addDoc(notificationsRef, {
          ...notification,
          read: false,
          readAt: null,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
      } catch (notifErr) {
        console.error('Error sending notification:', notifErr);
      }

      return { success: true };
    } catch (err) {
      console.error('Error rejecting return:', err);
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  // Request Repair
  const requestRepair = async (equipmentId, repairData) => {
    try {
      if (!currentUser || !currentTeam) {
        throw new Error('กรุณาเข้าสู่ระบบและเลือกทีม');
      }

      const equipmentRef = doc(db, 'equipment', equipmentId);
      const equipmentSnap = await getDoc(equipmentRef);

      if (!equipmentSnap.exists()) {
        throw new Error('ไม่พบอุปกรณ์');
      }

      const equipmentData = equipmentSnap.data();

      // Create repair request
      const repairRequest = {
        teamId: currentTeam.id,
        equipmentId: equipmentId,
        equipmentName: equipmentData.name,
        equipmentCode: equipmentData.code,
        reportedBy: currentUser.uid,
        reportedByName: currentUser.displayName || currentUser.email,
        problemDescription: repairData.problemDescription || '',
        problemType: repairData.problemType || 'OTHER',
        urgency: repairData.urgency || 'NORMAL',
        images: repairData.images || [],
        status: 'PENDING',
        assignedTo: null,
        assignedAt: null,
        completedAt: null,
        repairNotes: '',
        repairCost: null,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      const repairRef = await addDoc(collection(db, 'equipmentRepairs'), repairRequest);

      // Update equipment status to REPAIR
      await updateDoc(equipmentRef, {
        status: EQUIPMENT_STATUS.REPAIR,
        updatedAt: serverTimestamp()
      });

      // Send notification to team leaders
      const leaders = await getTeamLeaders(currentTeam.id);
      for (const leaderId of leaders) {
        try {
          const notificationsRef = collection(db, notificationModel.collection);
          const notification = notificationModel.createNotification(
            leaderId,
            notificationModel.types.EQUIPMENT_REPAIR_REQUESTED || 'equipment_repair_requested',
            '🔧 มีคำขอส่งซ่อมอุปกรณ์',
            `${currentUser.displayName || currentUser.email} ส่งคำขอซ่อม "${equipmentData.name}"`,
            {
              repairId: repairRef.id,
              equipmentId: equipmentId,
              teamId: currentTeam.id,
              urgency: repairData.urgency || 'NORMAL'
            }
          );
          await addDoc(notificationsRef, {
            ...notification,
            read: false,
            readAt: null,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp()
          });
        } catch (notifErr) {
          console.error('Error sending notification to leader:', notifErr);
        }
      }

      return { success: true, repairId: repairRef.id };
    } catch (err) {
      console.error('Error requesting repair:', err);
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  // Update Repair Request (only if PENDING and owner)
  const updateRepair = async (repairId, updateData) => {
    try {
      if (!currentUser || !currentTeam) {
        throw new Error('กรุณาเข้าสู่ระบบและเลือกทีม');
      }

      const repairRef = doc(db, 'equipmentRepairs', repairId);
      const repairSnap = await getDoc(repairRef);

      if (!repairSnap.exists()) {
        throw new Error('ไม่พบรายการซ่อม');
      }

      const repairData = repairSnap.data();

      // Check if user is the owner
      if (repairData.reportedBy !== currentUser.uid) {
        throw new Error('คุณไม่มีสิทธิ์แก้ไขรายการนี้');
      }

      // Only allow update if status is PENDING
      if (repairData.status !== 'PENDING') {
        throw new Error('ไม่สามารถแก้ไขรายการที่ได้รับการอนุมัติแล้ว');
      }

      // Update repair request
      await updateDoc(repairRef, {
        problemDescription: updateData.problemDescription || repairData.problemDescription,
        problemType: updateData.problemType || repairData.problemType,
        urgency: updateData.urgency || repairData.urgency,
        images: updateData.images !== undefined ? updateData.images : repairData.images,
        updatedAt: serverTimestamp()
      });

      return { success: true };
    } catch (err) {
      console.error('Error updating repair:', err);
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  // Approve Repair (Leader only)
  const approveRepair = async (repairId) => {
    try {
      if (!currentUser || !currentTeam) {
        throw new Error('กรุณาเข้าสู่ระบบและเลือกทีม');
      }

      const userTeamRole = currentTeam.userRole;
      if (userTeamRole !== 'LEADER' && userTeamRole !== 'DEPUTY_LEADER') {
        throw new Error('คุณไม่มีสิทธิ์อนุมัติ');
      }

      const repairRef = doc(db, 'equipmentRepairs', repairId);
      const repairSnap = await getDoc(repairRef);

      if (!repairSnap.exists()) {
        throw new Error('ไม่พบรายการซ่อม');
      }

      const repairData = repairSnap.data();

      if (repairData.status === 'APPROVED' || repairData.status === 'REJECTED') {
        throw new Error(`รายการนี้ได้รับการ${repairData.status === 'APPROVED' ? 'อนุมัติ' : 'ปฏิเสธ'}แล้ว`);
      }

      // Update repair status
      await updateDoc(repairRef, {
        status: 'APPROVED',
        approvedBy: currentUser.uid,
        approvedByName: currentUser.displayName || currentUser.email,
        approvedAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      // Send notification to reporter
      try {
        const notificationsRef = collection(db, notificationModel.collection);
        const notification = notificationModel.createNotification(
          repairData.reportedBy,
          notificationModel.types.EQUIPMENT_REPAIR_APPROVED || 'equipment_repair_approved',
          '✅ คำขอส่งซ่อมได้รับการอนุมัติ',
          `คำขอส่งซ่อม "${repairData.equipmentName}" ได้รับการอนุมัติแล้ว`,
          {
            repairId: repairId,
            equipmentId: repairData.equipmentId,
            teamId: currentTeam.id
          }
        );
        await addDoc(notificationsRef, {
          ...notification,
          read: false,
          readAt: null,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
      } catch (notifErr) {
        console.error('Error sending notification:', notifErr);
      }

      return { success: true };
    } catch (err) {
      console.error('Error approving repair:', err);
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  // Reject Repair (Leader only)
  const rejectRepair = async (repairId, rejectionReason = '') => {
    try {
      if (!currentUser || !currentTeam) {
        throw new Error('กรุณาเข้าสู่ระบบและเลือกทีม');
      }

      const userTeamRole = currentTeam.userRole;
      if (userTeamRole !== 'LEADER' && userTeamRole !== 'DEPUTY_LEADER') {
        throw new Error('คุณไม่มีสิทธิ์ปฏิเสธ');
      }

      const repairRef = doc(db, 'equipmentRepairs', repairId);
      const repairSnap = await getDoc(repairRef);

      if (!repairSnap.exists()) {
        throw new Error('ไม่พบรายการซ่อม');
      }

      const repairData = repairSnap.data();

      if (repairData.status === 'APPROVED' || repairData.status === 'REJECTED') {
        throw new Error(`รายการนี้ได้รับการ${repairData.status === 'APPROVED' ? 'อนุมัติ' : 'ปฏิเสธ'}แล้ว`);
      }

      // Update repair status
      await updateDoc(repairRef, {
        status: 'REJECTED',
        rejectedBy: currentUser.uid,
        rejectedByName: currentUser.displayName || currentUser.email,
        rejectedAt: serverTimestamp(),
        rejectionReason: rejectionReason || '',
        updatedAt: serverTimestamp()
      });

      // Revert equipment status if it was set to REPAIR
      const equipmentRef = doc(db, 'equipment', repairData.equipmentId);
      const equipmentSnap = await getDoc(equipmentRef);
      if (equipmentSnap.exists()) {
        const equipmentData = equipmentSnap.data();
        // Only revert if status is REPAIR (might have been changed)
        if (equipmentData.status === EQUIPMENT_STATUS.REPAIR) {
          await updateDoc(equipmentRef, {
            status: EQUIPMENT_STATUS.AVAILABLE,
            updatedAt: serverTimestamp()
          });
        }
      }

      // Send notification to reporter
      try {
        const notificationsRef = collection(db, notificationModel.collection);
        const notification = notificationModel.createNotification(
          repairData.reportedBy,
          notificationModel.types.EQUIPMENT_REPAIR_REJECTED || 'equipment_repair_rejected',
          '❌ คำขอส่งซ่อมถูกปฏิเสธ',
          `คำขอส่งซ่อม "${repairData.equipmentName}" ถูกปฏิเสธ${rejectionReason ? `: ${rejectionReason}` : ''}`,
          {
            repairId: repairId,
            equipmentId: repairData.equipmentId,
            teamId: currentTeam.id,
            rejectionReason: rejectionReason || ''
          }
        );
        await addDoc(notificationsRef, {
          ...notification,
          read: false,
          readAt: null,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
      } catch (notifErr) {
        console.error('Error sending notification:', notifErr);
      }

      return { success: true };
    } catch (err) {
      console.error('Error rejecting repair:', err);
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  // Request Repair Completion (Member can request, Leader approves)
  const requestRepairCompletion = async (repairId, completionData) => {
    try {
      if (!currentUser || !currentTeam) {
        throw new Error('กรุณาเข้าสู่ระบบและเลือกทีม');
      }

      const repairRef = doc(db, 'equipmentRepairs', repairId);
      const repairSnap = await getDoc(repairRef);

      if (!repairSnap.exists()) {
        throw new Error('ไม่พบรายการซ่อม');
      }

      const repairData = repairSnap.data();

      // Only allow requesting completion if status is APPROVED or IN_PROGRESS
      if (repairData.status !== 'APPROVED' && repairData.status !== 'IN_PROGRESS') {
        throw new Error(`ไม่สามารถส่งคำขอได้ รายการนี้อยู่ในสถานะ: ${repairData.status}`);
      }

      // Check if there's already a pending completion request
      if (repairData.completionRequestStatus === 'PENDING') {
        throw new Error('มีคำขอซ่อมเสร็จรอการอนุมัติอยู่แล้ว');
      }

      // Handle both old format (string) and new format (object)
      const isOldFormat = typeof completionData === 'string';
      const reason = isOldFormat ? completionData : (completionData.reason || '');
      const condition = isOldFormat ? null : (completionData.condition || null);
      const conditionPercentage = isOldFormat ? null : (completionData.conditionPercentage || null);
      const notes = isOldFormat ? '' : (completionData.notes || '');
      const images = isOldFormat ? [] : (completionData.images || []);
      const cost = isOldFormat ? null : (completionData.cost || null);

      // Update repair with completion request
      const updateData = {
        completionRequestStatus: 'PENDING',
        completionRequestReason: reason,
        completionRequestedBy: currentUser.uid,
        completionRequestedByName: currentUser.displayName || currentUser.email,
        completionRequestedAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      // Add new fields if provided
      if (condition) updateData.completionCondition = condition;
      if (conditionPercentage !== null) updateData.completionConditionPercentage = conditionPercentage;
      if (notes) updateData.completionNotes = notes;
      if (images && images.length > 0) updateData.completionImages = images;
      if (cost !== null && cost !== undefined) updateData.completionCost = cost;

      await updateDoc(repairRef, updateData);

      // Send notification to team leaders
      const leaders = await getTeamLeaders(currentTeam.id);
      for (const leaderId of leaders) {
        try {
          const notificationsRef = collection(db, notificationModel.collection);
          const notification = notificationModel.createNotification(
            leaderId,
            notificationModel.types.EQUIPMENT_REPAIR_COMPLETION_REQUESTED || 'equipment_repair_completion_requested',
            '📋 มีคำขอซ่อมเสร็จใหม่',
            `สมาชิก "${currentUser.displayName || currentUser.email}" ส่งคำขอซ่อมเสร็จสำหรับ "${repairData.equipmentName}"${reason ? `: ${reason}` : ''}`,
            {
              repairId: repairId,
              equipmentId: repairData.equipmentId,
              teamId: currentTeam.id,
              completionReason: reason || ''
            }
          );
          await addDoc(notificationsRef, {
            ...notification,
            read: false,
            readAt: null,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp()
          });
        } catch (notifErr) {
          console.error('Error sending notification:', notifErr);
        }
      }

      return { success: true };
    } catch (err) {
      console.error('Error requesting repair completion:', err);
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  // Approve Repair Completion Request (Leader only)
  const approveRepairCompletion = async (repairId) => {
    try {
      if (!currentUser || !currentTeam) {
        throw new Error('กรุณาเข้าสู่ระบบและเลือกทีม');
      }

      const userTeamRole = currentTeam.userRole;
      if (userTeamRole !== 'LEADER' && userTeamRole !== 'DEPUTY_LEADER') {
        throw new Error('คุณไม่มีสิทธิ์อนุมัติ');
      }

      const repairRef = doc(db, 'equipmentRepairs', repairId);
      const repairSnap = await getDoc(repairRef);

      if (!repairSnap.exists()) {
        throw new Error('ไม่พบรายการซ่อม');
      }

      const repairData = repairSnap.data();

      // Check if there's a pending completion request
      if (repairData.completionRequestStatus !== 'PENDING') {
        throw new Error('ไม่มีคำขอซ่อมเสร็จที่รอการอนุมัติ');
      }

      // Update repair status to COMPLETED
      const updateData = {
        status: 'COMPLETED',
        completionRequestStatus: 'APPROVED',
        completedAt: serverTimestamp(),
        completedBy: repairData.completionRequestedBy || currentUser.uid,
        completedByName: repairData.completionRequestedByName || currentUser.displayName || currentUser.email,
        completionApprovedBy: currentUser.uid,
        completionApprovedByName: currentUser.displayName || currentUser.email,
        completionApprovedAt: serverTimestamp(),
        completionDate: serverTimestamp(), // For dashboard reporting
        updatedAt: serverTimestamp()
      };

      // Preserve completion condition data if exists
      if (repairData.completionCondition) updateData.completionCondition = repairData.completionCondition;
      if (repairData.completionConditionPercentage !== null && repairData.completionConditionPercentage !== undefined) {
        updateData.completionConditionPercentage = repairData.completionConditionPercentage;
      }
      if (repairData.completionNotes) updateData.completionNotes = repairData.completionNotes;
      if (repairData.completionImages && repairData.completionImages.length > 0) {
        updateData.completionImages = repairData.completionImages;
      }
      if (repairData.completionCost !== null && repairData.completionCost !== undefined) {
        updateData.completionCost = repairData.completionCost;
      }

      await updateDoc(repairRef, updateData);

      // Send notification to requester
      if (repairData.completionRequestedBy) {
        try {
          const notificationsRef = collection(db, notificationModel.collection);
          const notification = notificationModel.createNotification(
            repairData.completionRequestedBy,
            notificationModel.types.EQUIPMENT_REPAIR_COMPLETED || 'equipment_repair_completed',
            '✅ คำขอซ่อมเสร็จได้รับการอนุมัติ',
            `คำขอซ่อมเสร็จสำหรับ "${repairData.equipmentName}" ได้รับการอนุมัติแล้ว สามารถรับกลับได้`,
            {
              repairId: repairId,
              equipmentId: repairData.equipmentId,
              teamId: currentTeam.id
            }
          );
          await addDoc(notificationsRef, {
            ...notification,
            read: false,
            readAt: null,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp()
          });
        } catch (notifErr) {
          console.error('Error sending notification:', notifErr);
        }
      }

      return { success: true };
    } catch (err) {
      console.error('Error approving repair completion:', err);
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  // Mark Repair as Completed (Leader only - direct completion without request)
  const markRepairAsCompleted = async (repairId) => {
    try {
      if (!currentUser || !currentTeam) {
        throw new Error('กรุณาเข้าสู่ระบบและเลือกทีม');
      }

      const userTeamRole = currentTeam.userRole;
      if (userTeamRole !== 'LEADER' && userTeamRole !== 'DEPUTY_LEADER') {
        throw new Error('คุณไม่มีสิทธิ์อัปเดตสถานะ');
      }

      const repairRef = doc(db, 'equipmentRepairs', repairId);
      const repairSnap = await getDoc(repairRef);

      if (!repairSnap.exists()) {
        throw new Error('ไม่พบรายการซ่อม');
      }

      const repairData = repairSnap.data();

      // Only allow marking as completed if status is APPROVED or IN_PROGRESS
      if (repairData.status !== 'APPROVED' && repairData.status !== 'IN_PROGRESS') {
        throw new Error(`ไม่สามารถอัปเดตสถานะได้ รายการนี้อยู่ในสถานะ: ${repairData.status}`);
      }

      // Update repair status to COMPLETED
      await updateDoc(repairRef, {
        status: 'COMPLETED',
        completedAt: serverTimestamp(),
        completedBy: currentUser.uid,
        completedByName: currentUser.displayName || currentUser.email,
        updatedAt: serverTimestamp()
      });

      // Send notification to reporter
      try {
        const notificationsRef = collection(db, notificationModel.collection);
        const notification = notificationModel.createNotification(
          repairData.reportedBy,
          notificationModel.types.EQUIPMENT_REPAIR_COMPLETED || 'equipment_repair_completed',
          '✅ อุปกรณ์ซ่อมเสร็จแล้ว',
          `อุปกรณ์ "${repairData.equipmentName}" ซ่อมเสร็จแล้ว สามารถรับกลับได้`,
          {
            repairId: repairId,
            equipmentId: repairData.equipmentId,
            teamId: currentTeam.id
          }
        );
        await addDoc(notificationsRef, {
          ...notification,
          read: false,
          readAt: null,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
      } catch (notifErr) {
        console.error('Error sending notification:', notifErr);
      }

      return { success: true };
    } catch (err) {
      console.error('Error marking repair as completed:', err);
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  // Receive Repair Equipment (anyone can receive, needs leader approval)
  const receiveRepair = async (repairId, receiveData) => {
    try {
      if (!currentUser || !currentTeam) {
        throw new Error('กรุณาเข้าสู่ระบบและเลือกทีม');
      }

      const repairRef = doc(db, 'equipmentRepairs', repairId);
      const repairSnap = await getDoc(repairRef);

      if (!repairSnap.exists()) {
        throw new Error('ไม่พบรายการซ่อม');
      }

      const repairData = repairSnap.data();

      // Check if repair is completed
      if (repairData.status !== 'COMPLETED') {
        throw new Error('อุปกรณ์ยังซ่อมไม่เสร็จ');
      }

      // Check if already received
      if (repairData.status === 'RECEIVED' || repairData.receiveStatus === 'APPROVED') {
        throw new Error('อุปกรณ์นี้ได้รับการรับกลับแล้ว');
      }

      // Check if there's already a pending receive request
      if (repairData.receiveStatus === 'PENDING') {
        throw new Error('มีคำขอรับกลับรอการอนุมัติอยู่แล้ว');
      }

      // Create receive request
      await updateDoc(repairRef, {
        receiveStatus: 'PENDING',
        receivedBy: currentUser.uid,
        receivedByName: currentUser.displayName || currentUser.email,
        receiveRequestedAt: serverTimestamp(),
        receiveCondition: receiveData.condition || 'GOOD',
        receiveDescription: receiveData.description || '',
        receiveImages: receiveData.images || [],
        updatedAt: serverTimestamp()
      });

      // Send notification to team leaders
      const leaders = await getTeamLeaders(currentTeam.id);
      for (const leaderId of leaders) {
        try {
          const notificationsRef = collection(db, notificationModel.collection);
          const notification = notificationModel.createNotification(
            leaderId,
            notificationModel.types.EQUIPMENT_REPAIR_RECEIVE_REQUESTED || 'equipment_repair_receive_requested',
            '📥 มีคำขอรับกลับอุปกรณ์จากการซ่อม',
            `${currentUser.displayName || currentUser.email} ขอรับกลับ "${repairData.equipmentName}" จากการซ่อม`,
            {
              repairId: repairId,
              equipmentId: repairData.equipmentId,
              teamId: currentTeam.id
            }
          );
          await addDoc(notificationsRef, {
            ...notification,
            read: false,
            readAt: null,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp()
          });
        } catch (notifErr) {
          console.error('Error sending notification to leader:', notifErr);
        }
      }

      return { success: true };
    } catch (err) {
      console.error('Error receiving repair:', err);
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  // Approve Receive Repair (Leader only)
  const approveReceiveRepair = async (repairId) => {
    try {
      if (!currentUser || !currentTeam) {
        throw new Error('กรุณาเข้าสู่ระบบและเลือกทีม');
      }

      const userTeamRole = currentTeam.userRole;
      if (userTeamRole !== 'LEADER' && userTeamRole !== 'DEPUTY_LEADER') {
        throw new Error('คุณไม่มีสิทธิ์อนุมัติ');
      }

      const repairRef = doc(db, 'equipmentRepairs', repairId);
      const repairSnap = await getDoc(repairRef);

      if (!repairSnap.exists()) {
        throw new Error('ไม่พบรายการซ่อม');
      }

      const repairData = repairSnap.data();

      if (repairData.receiveStatus !== 'PENDING') {
        throw new Error('คำขอนี้ไม่อยู่ในสถานะที่สามารถอนุมัติได้');
      }

      // Update repair status to RECEIVED
      await updateDoc(repairRef, {
        receiveStatus: 'APPROVED',
        receiveApprovedBy: currentUser.uid,
        receiveApprovedByName: currentUser.displayName || currentUser.email,
        receiveApprovedAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      // Update equipment status based on receive condition
      const equipmentRef = doc(db, 'equipment', repairData.equipmentId);
      const equipmentSnap = await getDoc(equipmentRef);
      
      if (equipmentSnap.exists()) {
        let newStatus = EQUIPMENT_STATUS.AVAILABLE;
        if (repairData.receiveCondition === 'DAMAGED') {
          newStatus = EQUIPMENT_STATUS.DAMAGED;
        } else if (repairData.receiveCondition === 'NEEDS_REPAIR') {
          newStatus = EQUIPMENT_STATUS.REPAIR;
        }

        await updateDoc(equipmentRef, {
          status: newStatus,
          updatedAt: serverTimestamp()
        });
      }

      // Send notification to receiver
      if (repairData.receivedBy) {
        try {
          const notificationsRef = collection(db, notificationModel.collection);
          const notification = notificationModel.createNotification(
            repairData.receivedBy,
            notificationModel.types.EQUIPMENT_REPAIR_RECEIVE_APPROVED || 'equipment_repair_receive_approved',
            '✅ คำขอรับกลับอุปกรณ์ได้รับการอนุมัติ',
            `คำขอรับกลับ "${repairData.equipmentName}" จากการซ่อมได้รับการอนุมัติแล้ว`,
            {
              repairId: repairId,
              equipmentId: repairData.equipmentId,
              teamId: currentTeam.id
            }
          );
          await addDoc(notificationsRef, {
            ...notification,
            read: false,
            readAt: null,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp()
          });
        } catch (notifErr) {
          console.error('Error sending notification:', notifErr);
        }
      }

      return { success: true };
    } catch (err) {
      console.error('Error approving receive repair:', err);
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  // Reject Receive Repair (Leader only)
  const rejectReceiveRepair = async (repairId, rejectionReason = '') => {
    try {
      if (!currentUser || !currentTeam) {
        throw new Error('กรุณาเข้าสู่ระบบและเลือกทีม');
      }

      const userTeamRole = currentTeam.userRole;
      if (userTeamRole !== 'LEADER' && userTeamRole !== 'DEPUTY_LEADER') {
        throw new Error('คุณไม่มีสิทธิ์ปฏิเสธ');
      }

      const repairRef = doc(db, 'equipmentRepairs', repairId);
      const repairSnap = await getDoc(repairRef);

      if (!repairSnap.exists()) {
        throw new Error('ไม่พบรายการซ่อม');
      }

      const repairData = repairSnap.data();

      if (repairData.receiveStatus !== 'PENDING') {
        throw new Error('คำขอนี้ไม่อยู่ในสถานะที่สามารถปฏิเสธได้');
      }

      // Update repair receive status to REJECTED
      await updateDoc(repairRef, {
        receiveStatus: 'REJECTED',
        receiveRejectedBy: currentUser.uid,
        receiveRejectedByName: currentUser.displayName || currentUser.email,
        receiveRejectedAt: serverTimestamp(),
        receiveRejectionReason: rejectionReason || '',
        updatedAt: serverTimestamp()
      });

      // Send notification to receiver
      if (repairData.receivedBy) {
        try {
          const notificationsRef = collection(db, notificationModel.collection);
          const notification = notificationModel.createNotification(
            repairData.receivedBy,
            notificationModel.types.EQUIPMENT_REPAIR_RECEIVE_REJECTED || 'equipment_repair_receive_rejected',
            '❌ คำขอรับกลับอุปกรณ์ถูกปฏิเสธ',
            `คำขอรับกลับ "${repairData.equipmentName}" จากการซ่อมถูกปฏิเสธ${rejectionReason ? `: ${rejectionReason}` : ''}`,
            {
              repairId: repairId,
              equipmentId: repairData.equipmentId,
              teamId: currentTeam.id,
              rejectionReason: rejectionReason || ''
            }
          );
          await addDoc(notificationsRef, {
            ...notification,
            read: false,
            readAt: null,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp()
          });
        } catch (notifErr) {
          console.error('Error sending notification:', notifErr);
        }
      }

      return { success: true };
    } catch (err) {
      console.error('Error rejecting receive repair:', err);
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  // Assign Equipment to Shift (for Shift Plan)
  const assignEquipmentToShift = async (planId, planVersion, shiftId, equipmentId, assignmentData) => {
    try {
      if (!currentUser || !currentTeam) {
        throw new Error('กรุณาเข้าสู่ระบบและเลือกทีม');
      }

      // Get equipment details
      const equipmentRef = doc(db, 'equipment', equipmentId);
      const equipmentSnap = await getDoc(equipmentRef);

      if (!equipmentSnap.exists()) {
        throw new Error('ไม่พบอุปกรณ์');
      }

      const equipmentData = equipmentSnap.data();

      // Create assignment
      const assignment = {
        teamId: currentTeam.id,
        planId: planId,
        planVersion: planVersion,
        shiftId: shiftId,
        equipmentId: equipmentId,
        equipmentCode: equipmentData.code,
        equipmentName: equipmentData.name,
        equipmentType: equipmentData.type,
        quantity: assignmentData.quantity || 1,
        assignedBy: currentUser.uid,
        assignedByName: currentUser.displayName || currentUser.email,
        assignedAt: serverTimestamp(),
        status: 'PLANNED',
        notes: assignmentData.notes || null,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      const assignmentRef = await addDoc(collection(db, 'equipmentAssignments'), assignment);

      return { success: true, assignmentId: assignmentRef.id };
    } catch (err) {
      console.error('Error assigning equipment to shift:', err);
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  // Get Equipment Assignments for Plan
  const getEquipmentAssignments = async (planId, planVersion = null) => {
    try {
      if (!currentTeam) {
        return [];
      }

      let assignmentsQuery = query(
        collection(db, 'equipmentAssignments'),
        where('teamId', '==', currentTeam.id),
        where('planId', '==', planId)
      );

      if (planVersion !== null) {
        assignmentsQuery = query(
          collection(db, 'equipmentAssignments'),
          where('teamId', '==', currentTeam.id),
          where('planId', '==', planId),
          where('planVersion', '==', planVersion)
        );
      }

      const snapshot = await getDocs(assignmentsQuery);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (err) {
      console.error('Error fetching equipment assignments:', err);
      setError(err.message);
      return [];
    }
  };

  // Remove Equipment Assignment
  const removeEquipmentAssignment = async (assignmentId) => {
    try {
      if (!currentUser || !currentTeam) {
        throw new Error('กรุณาเข้าสู่ระบบและเลือกทีม');
      }

      await deleteDoc(doc(db, 'equipmentAssignments', assignmentId));

      return { success: true };
    } catch (err) {
      console.error('Error removing equipment assignment:', err);
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  // Update Equipment Assignment
  const updateEquipmentAssignment = async (assignmentId, updateData) => {
    try {
      if (!currentUser || !currentTeam) {
        throw new Error('กรุณาเข้าสู่ระบบและเลือกทีม');
      }

      const assignmentRef = doc(db, 'equipmentAssignments', assignmentId);
      await updateDoc(assignmentRef, {
        ...updateData,
        updatedAt: serverTimestamp()
      });

      return { success: true };
    } catch (err) {
      console.error('Error updating equipment assignment:', err);
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  // Recalculate Stock from Approved Transactions
  const recalculateStock = async (equipmentId) => {
    try {
      if (!currentTeam) {
        throw new Error('กรุณาเลือกทีม');
      }

      const equipmentRef = doc(db, 'equipment', equipmentId);
      const equipmentSnap = await getDoc(equipmentRef);

      if (!equipmentSnap.exists()) {
        throw new Error('ไม่พบอุปกรณ์');
      }

      // Get all approved stock transactions for this equipment
      const transactionsQuery = query(
        collection(db, 'equipmentStockTransactions'),
        where('equipmentId', '==', equipmentId),
        where('status', '==', 'APPROVED')
      );
      const transactionsSnapshot = await getDocs(transactionsQuery);
      
      // Calculate total stock from approved transactions only
      let totalStock = 0;
      transactionsSnapshot.forEach(doc => {
        const tx = doc.data();
        if (tx.type === 'STOCK_IN' && tx.status === 'APPROVED') {
          totalStock += tx.quantity || 0;
        }
      });

      // Update equipment currentStock
      await updateDoc(equipmentRef, {
        currentStock: totalStock,
        updatedAt: serverTimestamp()
      });

      return { success: true, newStock: totalStock };
    } catch (err) {
      console.error('Error recalculating stock:', err);
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  // Get Pending Inspections for current user
  const getPendingInspections = async (targetDate = null) => {
    try {
      if (!currentUser || !currentTeam) {
        return [];
      }

      const today = targetDate || new Date();
      today.setHours(0, 0, 0, 0);
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);

      // Get user's shifts for today
      const shiftsQuery = query(
        collection(db, 'shifts'),
        where('teamId', '==', currentTeam.id),
        where('assignedTo', '==', currentUser.uid),
        where('startTime', '>=', Timestamp.fromDate(today)),
        where('startTime', '<', Timestamp.fromDate(tomorrow))
      );

      const shiftsSnapshot = await getDocs(shiftsQuery);
      const todayShifts = shiftsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      if (todayShifts.length === 0) {
        return [];
      }

      // Get all inspections for today
      const inspectionsQuery = query(
        collection(db, 'equipmentInspections'),
        where('teamId', '==', currentTeam.id),
        where('inspectedBy', '==', currentUser.uid)
      );

      const inspectionsSnapshot = await getDocs(inspectionsQuery);
      const allInspections = inspectionsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Filter inspections for today
      const todayInspections = allInspections.filter(inspection => {
        const inspectionDate = inspection.date?.toDate ? inspection.date.toDate() : new Date(inspection.date);
        return inspectionDate >= today && inspectionDate < tomorrow;
      });

      // Map rounds to shift types
      const roundToShiftType = {
        [INSPECTION_ROUNDS.MORNING_BEFORE]: 'MORNING',
        [INSPECTION_ROUNDS.MORNING_AFTER]: 'MORNING',
        [INSPECTION_ROUNDS.AFTERNOON_BEFORE]: 'AFTERNOON',
        [INSPECTION_ROUNDS.AFTERNOON_AFTER]: 'AFTERNOON',
        [INSPECTION_ROUNDS.NIGHT_BEFORE]: 'NIGHT',
        [INSPECTION_ROUNDS.NIGHT_AFTER]: 'NIGHT'
      };

      // Get all possible rounds
      const allRounds = Object.values(INSPECTION_ROUNDS);
      const pendingInspections = [];

      for (const shift of todayShifts) {
        const shiftType = shift.shiftType;
        const shiftStart = shift.startTime?.toDate ? shift.startTime.toDate() : new Date(shift.startTime);
        const shiftEnd = shift.endTime?.toDate ? shift.endTime.toDate() : new Date(shift.endTime);

        // Find rounds for this shift type
        const relevantRounds = allRounds.filter(round => roundToShiftType[round] === shiftType);

        for (const round of relevantRounds) {
          // Find existing inspection for this round
          const existingInspection = todayInspections.find(
            insp => insp.round === round && insp.shiftType === shiftType
          );

          // Determine status
          let status = 'NOT_STARTED';
          let inspectionId = null;
          let completedItems = 0;
          let totalItems = equipment.filter(eq => eq.type === EQUIPMENT_TYPES.SMALL_ITEM || eq.type === EQUIPMENT_TYPES.LARGE_ITEM).length;
          let progressPercent = 0;

          if (existingInspection) {
            inspectionId = existingInspection.id;
            completedItems = existingInspection.items?.length || 0;
            totalItems = existingInspection.totalItems || totalItems;
            progressPercent = totalItems > 0 ? (completedItems / totalItems) * 100 : 0;

            if (existingInspection.status === 'APPROVED') {
              status = 'COMPLETED';
            } else if (existingInspection.status === 'PENDING_APPROVAL') {
              status = 'COMPLETED'; // Submitted, waiting for approval
            } else if (existingInspection.status === 'DRAFT' && completedItems > 0) {
              status = 'IN_PROGRESS';
            } else {
              status = 'IN_PROGRESS';
            }
          }

          // Check if it's time for this round
          const now = new Date();
          const isBeforeRound = round.includes('_BEFORE');
          const isAfterRound = round.includes('_AFTER');
          
          let isTimeForRound = false;
          if (isBeforeRound) {
            // Before shift: should be done before shift starts
            isTimeForRound = now >= shiftStart && now < shiftEnd;
          } else if (isAfterRound) {
            // After shift: should be done after shift starts
            isTimeForRound = now >= shiftStart;
          }

          // Check if overdue
          const isOverdue = isTimeForRound && status !== 'COMPLETED' && now > shiftEnd;

          pendingInspections.push({
            round,
            shiftType,
            shiftTime: {
              start: shiftStart,
              end: shiftEnd
            },
            status: isOverdue ? 'OVERDUE' : status,
            inspectionId,
            totalItems,
            completedItems,
            progressPercent: Math.round(progressPercent),
            isTimeForRound,
            isOverdue
          });
        }
      }

      // Sort by round order
      const roundOrder = [
        INSPECTION_ROUNDS.MORNING_BEFORE,
        INSPECTION_ROUNDS.MORNING_AFTER,
        INSPECTION_ROUNDS.AFTERNOON_BEFORE,
        INSPECTION_ROUNDS.AFTERNOON_AFTER,
        INSPECTION_ROUNDS.NIGHT_BEFORE,
        INSPECTION_ROUNDS.NIGHT_AFTER
      ];

      return pendingInspections.sort((a, b) => {
        const indexA = roundOrder.indexOf(a.round);
        const indexB = roundOrder.indexOf(b.round);
        return indexA - indexB;
      });
    } catch (err) {
      console.error('Error getting pending inspections:', err);
      return [];
    }
  };

  const value = {
    equipment,
    catalogs,
    loading,
    error,
    createEquipment,
    createEquipmentFromCatalog,
    approveEquipment,
    rejectEquipment,
    updateEquipment,
    deleteEquipment,
    changeEquipmentStatus,
    stockIn,
    approveStockIn,
    rejectStockIn,
    recalculateStock,
    createInspection,
    submitInspection,
    updateInspection,
    approveInspection,
    rejectInspection,
    borrowEquipment,
    approveBorrow,
    returnEquipment,
    assignEquipmentToShift,
    getEquipmentAssignments,
    removeEquipmentAssignment,
    updateEquipmentAssignment,
    getPendingInspections,
    rejectBorrow,
    requestRepair,
    updateRepair,
    approveRepair,
    rejectRepair,
    requestRepairCompletion,
    approveRepairCompletion,
    markRepairAsCompleted,
    receiveRepair,
    approveReceiveRepair,
    rejectReceiveRepair,
    approveReturn,
    rejectReturn
  };

  return (
    <EquipmentContext.Provider value={value}>
      {children}
    </EquipmentContext.Provider>
  );
};


